import { StudyStartDateCriteria, BookingSystemCriteria, ProductFilters, ProductFilterSection, EmptyCriteria } from "app/pages/DiscountEditor/modules/AdditionalCriteria/state";
import { Action, ActionType, AdditionalCriteriaType } from "app/pages/DiscountEditor/actionTypes";
import { combineReducers } from "redux";
import { ProductFilterType, getProductType, parseProductFilterDto } from "app/shared/model/ProductFilter";
import { ProductType } from "app/shared/model";
import { UnitsFilterType, parseUnitsFilterDto } from "app/shared/model/ProductUnitFilter";
import { ProductSelectionDto } from "app/shared/model/DiscountDto";
import { isDefined } from "app/helpers/objectHelper";

function addOrEditModal(state: AdditionalCriteriaType|undefined, action: Action) : AdditionalCriteriaType {
    if (!isDefined(state))
        state = AdditionalCriteriaType.None;

    switch(action.type) {
        case ActionType.SetAddOrEditCriteriaModal:
            return action.modal

        case ActionType.SaveStudentStartDateCriteria: 
        case ActionType.SetAdditionalCriteriaProductFilter: 
        case ActionType.SaveBookingSystemsCriteria: 
            return AdditionalCriteriaType.None;

        default: 
            return state; 
    }
}

function deleteConfirmationModal(state: AdditionalCriteriaType|undefined, action: Action) : AdditionalCriteriaType {
    if (!isDefined(state))
        state = AdditionalCriteriaType.None;
    
    switch(action.type) {
        case ActionType.DeleteAdditionalCriteria: 
            return AdditionalCriteriaType.None
        case ActionType.SetDeleteCriteriaConfirmationModal: 
            return action.criteriaType
        default: 
            return state; 
    }
}

function studyStartDate(state: StudyStartDateCriteria|undefined, action: Action) : StudyStartDateCriteria {
    if (!isDefined(state))
        state = { hasCriteria: false }; 

    switch(action.type) {
        case ActionType.PageLoaded: 
            const criteria = action.discount.additionalCriteria.studyStartDate; 
            
            if (!isDefined(criteria)) {
                return { hasCriteria: false };
            }

            if (!isDefined(criteria.maxDate) && !isDefined(criteria.minDate)) {
                return { hasCriteria: false };
            }
                        
            const unitsType = isDefined(criteria.minDate) && isDefined(criteria.maxDate)
                ? UnitsFilterType.BetweenUnits
                : isDefined(criteria.minDate) ? UnitsFilterType.MinUnits : UnitsFilterType.MaxUnits

            return {
                hasCriteria: true, 
                unitsType: unitsType, 
                minDate: criteria.minDate, 
                maxDate: criteria.maxDate 
            }
        case ActionType.SaveStudentStartDateCriteria: 
            return action.criteria
        case ActionType.DeleteAdditionalCriteria: 
            return (action.criteriaType === AdditionalCriteriaType.StudyStartDate)
                ? { hasCriteria: false }
                : state;
        default: 
            return state; 
    }
}

function normalizeBookingSystemId(id: string | number | null | undefined): string {
    if (id === null || id === undefined) {
        return '';
    }
    const str = String(id).trim();
    const match = /^BookingSystem-(.+)$/i.exec(str);
    return match ? match[1] : str;
}

function bookingSystemIdsMatch(a: string | number, b: string | number): boolean {
    const left = normalizeBookingSystemId(a);
    const right = normalizeBookingSystemId(b);
    if (!left || !right) {
        return false;
    }
    return left === right;
}

function bookingSystem(state: BookingSystemCriteria|undefined, action: Action) : BookingSystemCriteria {
    if (!isDefined(state))
        state = { hasCriteria: false }; 

    switch(action.type) {
        case ActionType.PageLoaded: 
            const crtieria = action.discount.additionalCriteria.bookingSystem

            if (!isDefined(crtieria))
                return  { hasCriteria: false }

            const savedIds = crtieria.bookingSystemIds;
            if (!Array.isArray(savedIds) || savedIds.length === 0)
                return { hasCriteria: false }
                        
            const selectedSystems = action.data.bookingSystems
                .filter(x => savedIds.some(y => bookingSystemIdsMatch(y, x.id)));
            
            return {
                hasCriteria: true, 
                selectedSystems: selectedSystems
            };  
        case ActionType.SaveBookingSystemsCriteria:
            return action.criteria
        case ActionType.DeleteAdditionalCriteria: 
            return (action.criteriaType === AdditionalCriteriaType.BookingSystem)
                ? { hasCriteria: false }
                : state;
        default: 
            return state; 
    }
}

const emptyCriteria : EmptyCriteria = {hasCriteria: false};
const defaultProductFilters : ProductFilters = {
    courseFilter: emptyCriteria,
    accommodationFilter: emptyCriteria,
    addOnsFilter: emptyCriteria,
    transferFilter: emptyCriteria
}

function parseProductSelectionDto(dto: ProductSelectionDto|undefined) : ProductFilterSection|EmptyCriteria {
    if (!isDefined(dto))
        return { hasCriteria: false }; 

    return { 
        hasCriteria: true, 
        productFilter: parseProductFilterDto(dto.productFilter), 
        unitsFilter: parseUnitsFilterDto(dto.unitsFilter)
    }; 
}

function productFilters(state: ProductFilters|undefined, action: Action) : ProductFilters {
    if (!isDefined(state))
        state = defaultProductFilters;

    switch(action.type) {
        case ActionType.PageLoaded: 
            return {
                courseFilter: parseProductSelectionDto(
                    action.discount.additionalCriteria.requiredProducts.courseFilter), 
                accommodationFilter: parseProductSelectionDto(
                    action.discount.additionalCriteria.requiredProducts.accommodationFilter),
                transferFilter: parseProductSelectionDto(
                    action.discount.additionalCriteria.requiredProducts.transferFilter),  
                addOnsFilter: parseProductSelectionDto(
                    action.discount.additionalCriteria.requiredProducts.addOnFilter) 
            }
        case ActionType.DeleteAdditionalCriteria:

            switch(action.criteriaType) {
                case AdditionalCriteriaType.Course:
                    return {
                        ... state,
                        courseFilter: emptyCriteria
                    };

                case AdditionalCriteriaType.Accommodation:
                    return {
                        ... state,
                        accommodationFilter: emptyCriteria
                    };

                case AdditionalCriteriaType.AddOns:
                    return {
                        ... state,
                        addOnsFilter: emptyCriteria
                    };

                case AdditionalCriteriaType.Transfer:
                    return {
                        ... state,
                        transferFilter: emptyCriteria
                    };

                default:
                    return state;
            }

        case ActionType.SetAdditionalCriteriaProductFilter:
            const filter : ProductFilterSection = {
                hasCriteria: true,
                productFilter: action.productFilter,
                unitsFilter: action.unitsFilter
            };

            const productType = getProductType(action.productFilter);
            switch(productType) {
                case ProductType.Course:
                    return {
                        ... state,
                        courseFilter: filter
                    };

                case ProductType.Accommodation:
                    return {
                        ... state,
                        accommodationFilter: filter
                    };

                case ProductType.AddOn:
                    return {
                        ... state,
                        addOnsFilter: filter
                    };

                case ProductType.Transfer:
                    return {
                        ... state,
                        transferFilter: filter
                    };

                default:
                    return state;
            }

        default:
            return state;
    }
}

export const reducer = combineReducers({
    addOrEditModal,
    deleteConfirmationModal,
    studyStartDate,
    bookingSystem,
    productFilters
});